import { DateTime } from "luxon";
import { useNavigate } from "react-router-dom";

import { CameraIcon, PencilIcon, ChevronDownIcon } from "@heroicons/react/24/outline";

import { IVehicle } from "../../../models/Vehicle";
import formatDateTime from "../../../utils/formatDateTime";
import PriceFormat from "../../../utils/priceFormat";
import Card from "../../shared/Card/Card";
import VehicleIcons from "../../shared/VehicleIcons/VehicleIcons";
import Modal from "../../../layouts/Modal";
import { useEffect, useState } from "react";
import useVehiclesMutations from "../../../data/useVehiclesMutations";
import useDefaultCRUDHandlers from "../../../hooks/useDefaultCRUDHandlers";
import useNotification from "../../notifications/useNotifications";
import useLeadByVehicleId from "../../../data/useLeadByVehicleId";
import VehicleCollectionQuestions from "../../vehicles/VehicleCollectionQuestions";
import ExportOffersTableRow from "../../exports/ExportOffersTableRow";
import PartVehicleSalvage from "./PartVehicleSalvage";
import { useAuth } from "../../../contexts/AuthContext";
import VehicleRecalls from "../../vehicles/VehicleRecalls";

const SingleDetail = ({
  title,
  value,
}: {
  title: string;
  value: string | null | undefined;
}) => {
  return (
    <div className="py-2 grid grid-cols-3 gap-4">
      <dt className="text-sm font-medium text-gray-500">{title}</dt>
      <dd className="text-sm text-gray-900 col-span-2">{value}</dd>
    </div>
  );
};

export default function PartVehicleDetails({
  vehicle,
  price = null,
  collectionQuestions = false,
}: {
  vehicle: IVehicle;
  price?: number | null;
  collectionQuestions?: boolean;
}) {
  const navigate = useNavigate();
  const { lead } = useLeadByVehicleId("PartVehicleDetails");
  const { vrmRefresh, update } = useVehiclesMutations();
  const { saveHandlers } = useDefaultCRUDHandlers("Vehicle");
  const { addNotification } = useNotification();
  const [open, setOpen] = useState(false);
  const [exportsOpen, setExportsOpen] = useState(false);
  const [exportValuesOpen, setExportValuesOpen] = useState(false);
  const [vrm, setVrm] = useState(vehicle.vrm);
  const [ewwEngineOffer, setEwwEngineOffer] = useState(vehicle.enginesWorldwideEngineOffer ?? "");
  const [ewwVfeOffer, setEwwVfeOffer] = useState(vehicle.enginesWorldwideVfeOffer ?? "");
  const [vlaEngineOffer, setVlaEngineOffer] = useState(vehicle.vlaEngineOffer ?? "");
  const [vlaVfeOffer, setVlaVfeOffer] = useState(vehicle.vlaVfeOffer ?? "");
  const [isCardOpen, setIsCardOpen] = useState(true);
  const { loading, claims } = useAuth();
  const [justDriver, setJustDriver] = useState(false);

  const handleUpdateVrm = () => {
    //Uppercase and remove whitespace from vrm
    const updatedVehicle = {
      ...vehicle,
      vrm: vrm.toUpperCase().replace(/\s/g, ""),
    };

    vrmRefresh.mutate(updatedVehicle, {
      onSuccess: () => {
        setOpen(false);
        saveHandlers.onSuccess();
      },
      onError: () => {
        saveHandlers.onError();
      },
    });
  };

  const handleGoToLead = () => {
    lead.mutate(vehicle.id, {
      onSuccess: (lead) => {
        if (lead) {
          console.log(lead);
        }
      },
      onError: () => {
        addNotification({
          variant: "error",
          primaryText: "Failed to lookup lead",
        });
      },
    });
  };

  const openExportValuesModal = () => {
    setEwwEngineOffer(vehicle.enginesWorldwideEngineOffer ?? "");
    setEwwVfeOffer(vehicle.enginesWorldwideVfeOffer ?? "");
    setVlaEngineOffer(vehicle.vlaEngineOffer ?? "");
    setVlaVfeOffer(vehicle.vlaVfeOffer ?? "");
    setExportValuesOpen(true);
  };

  const handleUpdateExportValues = () => {
    const updatedVehicle: IVehicle = {
      ...vehicle,
      enginesWorldwideEngineOffer: ewwEngineOffer || null,
      enginesWorldwideVfeOffer: ewwVfeOffer || null,
      vlaEngineOffer: vlaEngineOffer || null,
      vlaVfeOffer: vlaVfeOffer || null,
    };

    update.mutate(updatedVehicle, {
      onSuccess: () => {
        setExportValuesOpen(false);
        saveHandlers.onSuccess();
      },
      onError: () => {
        saveHandlers.onError();
      },
    });
  };

  useEffect(() => {
    if (loading) return;

    if (
      !claims?.isAdmin &&
      !claims?.isExporter &&
      !claims?.isMechanic &&
      !claims?.isStaff &&
      claims?.isDriver
    ) {
      setJustDriver(true);
    } else {
      setJustDriver(false);
    }
  }, [loading, claims]);

  return (
    <>
      <Modal open={exportsOpen} width={"max-w-2xl"} setOpen={setExportsOpen}>
        <ExportOffersTableRow vehicle={vehicle} />
      </Modal>
      <Modal open={open} setOpen={setOpen} width="max-w-xl">
        <label>Enter VRM to search for:</label>
        <input
          defaultValue={vehicle.vrm}
          onChange={(e) => setVrm(e.target.value)}
          className="my-5 w-full text-sm"
          type="text"
        />
        <button
          onClick={() => handleUpdateVrm()}
          className="float-right rounded-md border border-transparent bg-primary-600 px-3 py-2 text-sm font-medium leading-4 text-white shadow-sm hover:bg-primary-700"
        >
          Save
        </button>
      </Modal>
      <Modal open={exportValuesOpen} setOpen={setExportValuesOpen} width="max-w-xl">
        <div className="space-y-4">
          <h3 className="text-lg font-medium text-gray-900">Edit Export Values</h3>
          <div>
            <label className="block text-sm font-medium text-gray-700">EWW Engine Offer</label>
            <input
              value={ewwEngineOffer}
              onChange={(e) => setEwwEngineOffer(e.target.value)}
              className="mt-1 w-full text-sm"
              type="number"
              step="0.01"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">EWW VFE Offer</label>
            <input
              value={ewwVfeOffer}
              onChange={(e) => setEwwVfeOffer(e.target.value)}
              className="mt-1 w-full text-sm"
              type="number"
              step="0.01"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">VLA Engine Offer</label>
            <input
              value={vlaEngineOffer}
              onChange={(e) => setVlaEngineOffer(e.target.value)}
              className="mt-1 w-full text-sm"
              type="number"
              step="0.01"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">VLA VFE Offer</label>
            <input
              value={vlaVfeOffer}
              onChange={(e) => setVlaVfeOffer(e.target.value)}
              className="mt-1 w-full text-sm"
              type="number"
              step="0.01"
            />
          </div>
          <button
            onClick={handleUpdateExportValues}
            className="float-right rounded-md border border-transparent bg-primary-600 px-3 py-2 text-sm font-medium leading-4 text-white shadow-sm hover:bg-primary-700"
          >
            Save
          </button>
        </div>
      </Modal>


      <div className="relative">
        <div className="bg-white shadow rounded-lg">
          <div
            className="px-4 py-5 sm:px-6 cursor-pointer select-none"
            onClick={() => setIsCardOpen(!isCardOpen)}
          >
            <h2 className="flex justify-between items-center text-lg font-medium leading-6 text-gray-900">
              <span>Vehicle Details</span>
              <div className="flex items-center gap-2">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/vehicles/${vehicle.id}/edit`);
                  }}
                  className="p-1.5 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded-md transition-colors"
                  title="Edit Vehicle"
                >
                  <PencilIcon className="h-5 w-5" />
                </button>
                <ChevronDownIcon
                  className={`h-6 w-6 text-gray-400 hover:text-gray-500 transition-transform duration-200 ease-in-out ${isCardOpen ? '' : 'rotate-180'}`}
                />
              </div>
            </h2>
          </div>
          <div className={`transition-all duration-300 ease-in-out border-t border-gray-200 ${isCardOpen ? 'max-h-full opacity-100 p-4' : 'max-h-0 opacity-0 pointer-events-none'}`}>
            {collectionQuestions && (
              <>
                <VehicleCollectionQuestions vehicle={vehicle} />
                <hr className="my-4" />
              </>
            )}

            {!vehicle?.found && (
              <div className="mb-4">
                <p className="text-red-400 font-medium text-xl text-center">
                  VEHICLE NOT FOUND
                </p>

                <div className="flex justify-center w-full mt-2">
                  <button
                    onClick={() => setOpen(true)}
                    className="rounded-md border border-transparent bg-primary-600 px-3 py-2 text-sm font-medium leading-4 text-white shadow-sm hover:bg-primary-700"
                  >
                    Find Vehicle
                  </button>
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-8 ">
              <div className="col-span-1 flex flex-col justify-center items-center">
                <div className="flex justify-center relative">
                  {vehicle?.vehicleImageUrl ? (
                    <>
                      <img
                        className="w-40 h-28 object-contain"
                        src={`${vehicle != null ? vehicle.vehicleImageUrl : null}`}
                      />
                    </>
                  ) : (
                    <CameraIcon className="w-14 text-gray-600" />
                  )}
                </div>
                <div>
                  <div className="flex flex-col">
                    <SingleDetail
                      title={"Price"}
                      value={price ? PriceFormat(price) : "N/A"}
                    />
                    {price && <VehicleIcons vehicle={vehicle} hover />}
                  </div>
                </div>
              </div>

              {/* <div className="col-span-1 flex justify-center items-center">
          <div className="flex flex-col">
            <SingleDetail title={"Price"} value={price ? PriceFormat(price) : "N/A"} />
            {price && (
              <VehicleIcons vehicle={vehicle} hover />
            )}
          </div>
        </div> */}

              <dl className="grid col-span-1 lg:col-span-7 grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 divide-gray-200 items-center">
                <SingleDetail
                  title={"Make/Model"}
                  value={`${vehicle?.combinedMake} ${vehicle?.combinedModel}`}
                />
                {vehicle?.stockNumber && (
                  <SingleDetail title={"Stock Number"} value={vehicle.stockNumber} />
                )}
                {/* <SingleDetail title={"VRM"} value={vehicle?.vrm} /> */}
                <div className="py-2 grid grid-cols-3 gap-4">
                  <dt className="text-sm font-medium text-gray-500">VRM</dt>
                  <dd className="flex justify-between items-center text-sm text-gray-900 col-span-2">
                    <div>{vehicle.vrm}</div>
                    {!loading && !justDriver && (
                      <button
                        onClick={() => setOpen(true)}
                        className="inline-flex items-center rounded-md border border-transparent bg-primary-600 px-1 py-1 text-sm font-medium leading-4 text-white shadow-sm hover:bg-primary-700 mr-3"
                      >
                        Update
                      </button>
                    )}
                  </dd>
                </div>
                <SingleDetail
                  title={"Year"}
                  value={vehicle?.dvlaYearOfManufacture?.toString()}
                />
                <SingleDetail
                  title={"Engine Size"}
                  value={`${vehicle?.combinedEngineCapacity}cc`}
                />
                <SingleDetail
                  title={"Body Type"}
                  value={`${vehicle?.bodyStyleDescription} - ${vehicle.numberOfDoors} door`}
                />
                <SingleDetail
                  title={"Fuel Type"}
                  value={vehicle?.combinedFuelType}
                />
                <SingleDetail
                  title={"Colour Code"}
                  value={vehicle?.vinvisColourCode}
                />
                <SingleDetail
                  title={"Transmission Code"}
                  value={vehicle?.vinvisTransmissionCode}
                />
                <SingleDetail
                  title={"Transmission"}
                  value={`${vehicle?.combinedForwardGears} Speed ${vehicle?.combinedTransmission}`}
                />
                <SingleDetail
                  title={"Engine Code"}
                  value={vehicle?.engineModelCode}
                />
                <SingleDetail title={"Chassis No"} value={vehicle?.combinedVIN} />
                <SingleDetail
                  title={"Mileage"}
                  value={`${vehicle?.odometerInMiles} miles`}
                />
                <SingleDetail
                  title={"MOT Expiry"}
                  value={formatDateTime(
                    vehicle?.motExpiryDate,
                    DateTime.DATE_SHORT
                  )}
                />
                <SingleDetail
                  title={"Year Range"}
                  value={`${formatDateTime(vehicle.visibilityDate, {
                    year: "numeric",
                  })} - ${formatDateTime(vehicle.terminateDate, {
                    year: "numeric",
                  })}`}
                />
                <PartVehicleSalvage vehicle={vehicle} />
                {/* Only newer than 2011 */}
                <SingleDetail
                  title={"Catalytic Converter (Min-Avg-Max)"}
                  value={
                    vehicle.catalyticResults !== null &&
                      vehicle.catalyticResults.length > 0
                      ? `£${Math.min(
                        ...vehicle.catalyticResults.map((x) => x.value)
                      )} - £${vehicle.catalyticAveragePrice} - £${Math.max(
                        ...vehicle.catalyticResults.map((x) => x.value)
                      )}`
                      : "Unavailable"
                  }
                />
                <div onClick={openExportValuesModal} className="hover:bg-gray-300 cursor-pointer rounded-md">
                  <SingleDetail
                    title={"Export Values"}
                    value={`EWW Engine: ${PriceFormat(vehicle.enginesWorldwideEngineOffer)} - EWW VFE: ${PriceFormat(vehicle.enginesWorldwideVfeOffer)} - VLA Engine: ${PriceFormat(vehicle.vlaEngineOffer)} - VLA VFE: ${PriceFormat(vehicle.vlaVfeOffer)}`}
                  />
                </div>
                <div onClick={() => setExportsOpen(true)} className="hover:bg-gray-300 cursor-pointer rounded-md">
                  <SingleDetail
                    title={"Export Offers"}
                    value={`${vehicle.exportOffers?.length > 0 ? vehicle.exportOffers.length + " Offers" : "No Offers"}`}
                  />
                </div>
                {/* <div className="py-2 grid grid-cols-3 gap-4">
            <dt className="text-sm font-medium text-gray-500">Starts and Drives?</dt>
            <dd className="text-sm text-gray-900 col-span-2">
              {vehicle?.vehicleStarting ? "Yes" : "No"}
            </dd>
          </div> */}
              </dl>
            </div>
          </div>

          <div className="px-4">
            {vehicle?.found && <VehicleRecalls vehicle={vehicle} />}
          </div>
        </div>
      </div>
    </>
  );
}
